import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { completeTripRequest, resetCompleteTrip, getCompleteBookingByDateRequest } from "../../redux/actions/completeAction";
import { Button, Table, Input, Tooltip, DatePicker, message, ConfigProvider } from "antd";
import type { TableColumnsType } from "antd";
import { ExportOutlined, CarOutlined, RedoOutlined } from "@ant-design/icons";
import "../../style/completed.css";
import dayjs from "dayjs";
import { buildDatePayload } from "../../utils/dateUtils";
import { useBranchId } from "../../utils/hooks/useBranch";
import { useListAutoRefresh, BOOKING_LIST_EVENTS } from "../../utils/hooks/useListAutoRefresh";
import { exportToExcel, ExcelColumn } from "../../utils/excelExport";
import { COPY_CELL } from "../../utils/copyGuard";
import { noRowSelectionBgTheme } from "../../utils/tableTheme";

interface DataType {
  key: string;
  [key: string]: any;
}
const { RangePicker } = DatePicker;
const { Search } = Input;

const pageWrapper: React.CSSProperties = {
  padding: 0,
  background: "linear-gradient(135deg,#f7f2fb,#fbf7ff)",
  minHeight: "clamp(520px, 78vh, 900px)",
  display: "flex",
  flexDirection: "column",
};

const Completed: React.FC = () => {
  const dispatch = useDispatch();
  const roleid = useSelector((state: any) => state.auth.roleid);
  const {
    success: completeTripSuccess,
    error: completeTripError,
    completeBookings,
    completeBookingsLoading,
    tablelength,
    totalamount,
  } = useSelector((state: any) => state.complete || {});
  const branchId = useBranchId();
  const [selectedRowKey, setSelectedRowKey] = useState<string | null>(null);
  const tableClass = "Live-table";
  const today = dayjs();
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [searchText, setSearchText] = useState("");
  const lastFetchedRangeRef = useRef<string | null>(null);

  const columns: TableColumnsType<DataType> = [
    {
      title: "Book ID",
      dataIndex: "id",
      key: "id",
      className: COPY_CELL,
      align: "center",
      width: 110,
      fixed: "left",
      render: (text) => <strong style={{ color: "#89339c" }}>{text}</strong>,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      className: COPY_CELL,
      align: "center",
      width: 130,
      fixed: "left",
    },
    {
      title: "Mobile",
      dataIndex: "mobileno",
      key: "mobileno",
      className: COPY_CELL,
      align: "center",
      width: 130,
      fixed: "left",
      render: (val: string) => {
        if (!val) return "-";
        if (roleid === 4 || roleid === 5) {
          return "X".repeat(val.length);
        }
        return val;
      },
    },
    {
      title: "Cab ID",
      dataIndex: "cabid",
      key: "cabid",
      align: "center",
      width: 110,
    },
    {
      title: "Distance",
      dataIndex: "distance",
      key: "distance",
      align: "center",
      width: 110,
    },
    {
      title: "Duration",
      dataIndex: "duration",
      key: "duration",
      align: "center",
      width: 110,
    },
    {
      title: "Fare",
      dataIndex: "fare",
      key: "fare",
      align: "center",
      width: 100,
    },
    {
      title: "Date",
      dataIndex: "bookedtime",
      key: "bookedtime",
      align: "center",
      width: 120,
      render: (text: string) => <div>{dayjs(text).format("DD/MM/YYYY")}</div>
    },
    {
      title: "Time",
      key: "time",
      align: "center",
      width: 150,
      render: (_: any, record: any) => (
        <div>
          {record.bookedtime && <div>B - {dayjs(record.bookedtime).format("hh:mm A")}</div>}
          {record.assignedtime && <div>A - {dayjs(record.assignedtime).format("hh:mm A")}</div>}
          {record.completedtime && <div>C - {dayjs(record.completedtime).format("hh:mm A")}</div>}
        </div>
      ),
    },
    {
      title: "From Location",
      dataIndex: "fromloc",
      key: "fromloc",
      align: "center",
      width: 120,
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text} placement="topLeft">
          <span>{text || "-"}</span>
        </Tooltip>
      ),
    },
    {
      title: "To Location",
      dataIndex: "toloc",
      key: "toloc",
      align: "center",
      width: 120,
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text} placement="topLeft">
          <span>{text || "-"}</span>
        </Tooltip>
      ),
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      align: "center",
      width: "auto",
    },
    {
      title: "Vehicle Type",
      dataIndex: "vehicletype",
      key: "vehicletype",
      align: "center",
      width: 150,
    },
    {
      title: "Fare Type",
      dataIndex: "faretype",
      key: "faretype",
      align: "center",
      width: 150,
    },
    {
      title: "Credit",
      dataIndex: "iscredit",
      key: "iscredit",
      align: "center",
      width: 90,
      render: (val) => val ? "Yes" : "No",
    },
    {
      title: "Goods",
      dataIndex: "goods",
      key: "goods",
      align: "center",
      width: 150,
    },
    {
      title: "OTP",
      dataIndex: "otp",
      key: "otp",
      align: "center",
      width: 90,
    },
    {
      title: "Booked By",
      dataIndex: "bookedby",
      key: "bookedby",
      align: "center",
      width: 150,
      render: (_: any, record: any) => (
        <div>
          {record.bookedby && <div>B - {record.bookedby}</div>}
          {record.updatedby && <div>U - {record.updatedby}</div>}
        </div>
      ),
    },
    {
      title: "Vehicle Id",
      dataIndex: "vehicleid",
      key: "vehicleid",
      align: "center",
      width: 120,
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
      align: "center",
      width: 130,
    },
  ];

  useEffect(() => {
    if (completeTripSuccess) {
      message.success("Trip marked as completed!");
      dispatch(resetCompleteTrip());
      fetchCompletedBookings();
    } else if (completeTripError) {
      message.error(completeTripError);
      dispatch(resetCompleteTrip());
    }
  }, [completeTripSuccess, completeTripError, dispatch]);

  const fetchCompletedBookings = () => {
    const { fromDate: start, toDate: end } = buildDatePayload(fromDate, toDate.add(1, "day"));
    dispatch(getCompleteBookingByDateRequest(branchId, start, end));
  };

  useListAutoRefresh(BOOKING_LIST_EVENTS, fetchCompletedBookings);

  const handleRefresh = () => {
    setSearchText("")
    setSelectedRowKey(null);
    fetchCompletedBookings();
  };

  useEffect(() => {
    if (!fromDate || !toDate) return;
    const rangeKey = `${branchId}|${fromDate.valueOf()}|${toDate.valueOf()}`;
    if (lastFetchedRangeRef.current === rangeKey) return;
    lastFetchedRangeRef.current = rangeKey;
    fetchCompletedBookings();
  }, [dispatch, branchId, fromDate, toDate]);

  const rowSelection = {
    type: "radio" as const,
    selectedRowKeys: selectedRowKey ? [selectedRowKey] : [],
    onChange: (keys: React.Key[]) =>
      setSelectedRowKey(keys[0]?.toString() || null),
  };

  const filteredBookings = completeBookings.filter((booking: DataType) => {

    if (!searchText) return true;

    const lower = searchText.toLowerCase();

    return (
      booking.id?.toString().toLowerCase().includes(lower) ||
      booking.name?.toLowerCase().includes(lower) ||
      booking.mobileno?.toString().toLowerCase().includes(lower)
    );
  });

  const handleExport = () => {
    const columnsToExport: ExcelColumn[] = [
      { header: "Book ID", key: "id" },
      { header: "Name", key: "name" },
      {
        header: "Mobile",
        formatter: (row) => {
          if (!row.mobileno) return "-";
          if (roleid === 4 || roleid === 5) {
            return "X".repeat(String(row.mobileno).length);
          }
          return row.mobileno;
        },
      },
      { header: "Cab ID", key: "cabid" },
      { header: "Distance", key: "distance" },
      { header: "Duration", key: "duration" },
      { header: "Fare", key: "fare" },
      {
        header: "Date",
        formatter: (row) => (row.bookedtime ? dayjs(row.bookedtime).format("DD/MM/YYYY") : "-"),
      },
      {
        header: "Booked Time",
        formatter: (row) => (row.bookedtime ? dayjs(row.bookedtime).format("hh:mm A") : "-"),
      },
      {
        header: "Assigned Time",
        formatter: (row) => (row.assignedtime ? dayjs(row.assignedtime).format("hh:mm A") : "-"),
      },
      {
        header: "Completed Time",
        formatter: (row) => (row.completedtime ? dayjs(row.completedtime).format("hh:mm A") : "-"),
      },
      { header: "From Location", key: "fromloc" },
      { header: "To Location", key: "toloc" },
      { header: "Remarks", key: "remarks" },
      { header: "Vehicle Type", key: "vehicletype" },
      { header: "Fare Type", key: "faretype" },
      {
        header: "Credit",
        formatter: (row) => (row.iscredit ? "Yes" : "No"),
      },
      { header: "Goods", key: "goods" },
      { header: "OTP", key: "otp" },
      { header: "Booked By", key: "bookedby" },
      { header: "Updated By", key: "updatedby" },
      { header: "Vehicle ID", key: "vehicleid" },
      { header: "City", key: "city" },
    ];

    exportToExcel(filteredBookings, columnsToExport, "Completed_Bookings");
  };

  return (
    <div style={pageWrapper}>
      <div className="toolbar-glass completed-toolbar">
        <div className="cmp-toolbar-left">

          <Search
            className="custom-search-complete"
            placeholder="Search Booking ID..."
            enterButton
            size="large"
            allowClear
          />
          <Tooltip title="Refresh">
            <Button className="btn-export" onClick={handleRefresh}>
              <RedoOutlined />
            </Button>
          </Tooltip>
        </div>

        <div className="cmp-toolbar-right">
          <div className="active-pill">
            <CarOutlined />
            Total Trips : <span className="pill-value">{tablelength}</span>
          </div>

          <div className="active-pill">
            ₹ <span className="pill-value">{Number(totalamount || 0).toLocaleString("en-IN")}</span>
          </div>

          <Search
            className="custom-search-complete"
            placeholder="Search "
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={(value) => setSearchText(value)}
            onClear={() =>
              setSearchText("")
            }
            allowClear
            enterButton
            size="large"
          />

          <Tooltip title="Export">
            <Button className="btn-export" onClick={handleExport}>
              <ExportOutlined />
            </Button>
          </Tooltip>
        </div>
      </div>

      <div style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
        <ConfigProvider theme={noRowSelectionBgTheme}>
        <Table<DataType>
          bordered
          className={tableClass}
          rowSelection={rowSelection}
          rowClassName={(record) => {
            const v = record.vehicletype?.toLowerCase() || "";
            if (v.includes("bolero")) return "bolero-row";
            if (v.includes("407")) return "row-407";
            if (v.includes("eicher")) return "row-eicher";
            return "";
          }}
          columns={columns}
          dataSource={filteredBookings.map((b: any, i: number) => ({
            ...b,
            key: String(b.id ?? i),
          }))}
          loading={completeBookingsLoading}
          rowKey="key"
          scroll={{
            x: 2600,
            y: "calc(95vh - 260px)",
          }}
          pagination={{
            pageSizeOptions: ["5", "10", "100"],
            showSizeChanger: true,
            defaultPageSize: 10,
          }}
        />
        </ConfigProvider>
      </div>
    </div>
  );
};

export default Completed;
